import { useDraggable, useDroppable } from '@dnd-kit/core';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { TCategoryProduct } from 'src/types/categories';
import { PRODUCT_TYPE } from 'src/types/product';
import { defaultDndDelay } from 'src/utils/dnd';
import { getTranslate, isDisabledByPos } from 'src/utils/helpers';
import { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Image from 'src/view/components/Image/Image';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import './style.scss';

interface IProps {
  index: number;
  item: TCategoryProduct;
  className?: string;
}

const DragIcon = () => {
  return <Icon name={Icons.DragHand} size={14} color={theme.colors.gray2} className="category-item__icon" />;
};

let timer: NodeJS.Timeout | null = null;

export const CategoryItem = (props: IProps): JSX.Element => {
  const { attributes, isDragging, listeners, setNodeRef: draggableRef } = useDraggable({ id: props.index.toString() });
  const { isOver, setNodeRef: droppableRef } = useDroppable({ id: props.index.toString() });
  const [tooltip, setTooltip] = useState<boolean>(false);

  const sortRestrictionAccess = integrationsStore.posRestrictions?.itemsSort;
  const disabledItemsSorting = isDisabledByPos(sortRestrictionAccess);

  useEffect(() => {
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const imageClickHandler = (mouseUp?: boolean) => {
    if (!disabledItemsSorting) return;

    if (timer && mouseUp) {
      clearTimeout(timer);
    } else if (!mouseUp) {
      timer = setTimeout(() => setTooltip(true), defaultDndDelay);
    }
  };

  const itemTypeText = useMemo((): string | undefined => {
    if (props.item.type === PRODUCT_TYPE.DEAL_COMBO) return 'combo_offer';
    if (props.item.type === PRODUCT_TYPE.MEAL_COMBO) return 'combo_meal';
    return undefined;
  }, [props.item.type]);

  const className = useMemo(() => {
    return classNames(
      'category-item px-2 py-1 mb-2',
      { 'category-item__drag-clone': isDragging, 'category-item__droppable': !isDragging && isOver },
      props.className
    );
  }, [isDragging, isOver, props.className]);

  const getRef = () => {
    if (props.index === 0 || disabledItemsSorting) return undefined;
    return isDragging ? draggableRef : droppableRef;
  };

  return (
    <div ref={getRef()} className={className}>
      <div className="d-flex flex-row align-items-center">
        <IntegrationManager
          setting={sortRestrictionAccess}
          withTooltip
          tooltipOptions={{
            position: TOOLTIP_POSITION.PORTAL,
            trigger: tooltip,
            disableTrigger: () => setTooltip(false),
          }}
        >
          {disabledItemsSorting ? (
            <div
              className="category-item__icon-wrapper py-1 pr-1 mr-2 cursor-not-allowed"
              onPointerUp={() => imageClickHandler(true)}
              onPointerDown={() => imageClickHandler()}
            >
              <DragIcon />
            </div>
          ) : (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div className="category-item__icon-wrapper py-1 pr-1 mr-2" {...listeners} {...attributes}>
              <DragIcon />
            </div>
          )}
        </IntegrationManager>

        <Image src={props.item.image} wrapperClassName="category-item__image mr-4 radius-12" />
        <p className=" family-semibold text-size-16 text-break">{getTranslate(props.item.name)}</p>
      </div>

      {itemTypeText ? <p className="category-item__type-text letter-spacing-12 ml-2">{localeStore.t(itemTypeText)}</p> : null}
    </div>
  );
};
